import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BookingService } from '@app/modules/booking-modules/booking.service';
import {
  PerformerApplicationVm,
  PerformerBookingApplicationStatus
} from '@app/modules/booking-modules/booking.model';
import { AppStaticURL } from '@app/app.settings';
import { ConfirmationService } from 'primeng/api';
import { finalize } from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'app-profile-booking-venue-applications',
  templateUrl: './profile-booking-venue-applications.component.html',
  styleUrls: ['./profile-booking-venue-applications.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProfileBookingVenueApplicationsComponent implements OnChanges {
  @Input() bookingCustomerId: string | null = null;

  readonly StaticURL = AppStaticURL;
  readonly pageSize = 20;
  readonly statusOptions = [
    { name: 'Все статусы', value: '' as const },
    { name: 'Ожидает', value: PerformerBookingApplicationStatus.Pending },
    { name: 'Принята', value: PerformerBookingApplicationStatus.Accepted },
    { name: 'Отклонена', value: PerformerBookingApplicationStatus.Declined },
    { name: 'Отозвана', value: PerformerBookingApplicationStatus.Withdrawn }
  ];

  loading = false;
  loadError: string | null = null;
  items: PerformerApplicationVm[] = [];
  page = 1;
  hasMore = false;
  from = '';
  to = '';
  status: PerformerBookingApplicationStatus | '' = '';
  actionLoadingId: string | null = null;

  constructor(
    private bookingService: BookingService,
    private confirmationService: ConfirmationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.bookingCustomerId && this.bookingCustomerId) {
      this.page = 1;
      this.loadApplications();
    }
  }

  onApplyFilters(): void {
    this.page = 1;
    this.loadApplications();
  }

  onResetFilters(): void {
    this.from = '';
    this.to = '';
    this.status = '';
    this.page = 1;
    this.loadApplications();
  }

  onPrevPage(): void {
    if (this.page <= 1 || this.loading) {
      return;
    }
    this.page -= 1;
    this.loadApplications();
  }

  onNextPage(): void {
    if (!this.hasMore || this.loading) {
      return;
    }
    this.page += 1;
    this.loadApplications();
  }

  canAccept(item: PerformerApplicationVm): boolean {
    return item.status === PerformerBookingApplicationStatus.Pending;
  }

  canDecline(item: PerformerApplicationVm): boolean {
    return item.status === PerformerBookingApplicationStatus.Pending;
  }

  performerImage(item: PerformerApplicationVm): string | null {
    const p = item.performer;
    const rel = p?.image?.thumbnailPath || p?.image?.path || p?.picPath || p?.avatarPath;
    return rel ? this.StaticURL + String(rel).replace(/^\//, '') : null;
  }

  accept(item: PerformerApplicationVm): void {
    const requestId = item.requestId;
    if (!this.bookingCustomerId || !requestId || !this.canAccept(item) || this.actionLoadingId) {
      return;
    }
    this.actionLoadingId = requestId;
    this.bookingService
      .acceptVenueApplication(this.bookingCustomerId, requestId, {})
      .pipe(
        finalize(() => {
          this.actionLoadingId = null;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        () => this.loadApplications(),
        err => {
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось принять заявку.';
          this.cdr.markForCheck();
        }
      );
  }

  decline(item: PerformerApplicationVm): void {
    const requestId = item.requestId;
    if (!this.bookingCustomerId || !requestId || !this.canDecline(item) || this.actionLoadingId) {
      return;
    }
    this.confirmationService.confirm({
      message: 'Отклонить заявку артиста?',
      accept: () => {
        this.actionLoadingId = requestId;
        this.bookingService
          .declineVenueApplication(this.bookingCustomerId!, requestId)
          .pipe(
            finalize(() => {
              this.actionLoadingId = null;
              this.cdr.markForCheck();
            }),
            untilDestroyed(this)
          )
          .subscribe(
            () => this.loadApplications(),
            err => {
              this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось отклонить заявку.';
              this.cdr.markForCheck();
            }
          );
      }
    });
  }

  statusLabel(status: PerformerBookingApplicationStatus | null | undefined): string {
    switch (status) {
      case PerformerBookingApplicationStatus.Pending:
        return 'Ожидает';
      case PerformerBookingApplicationStatus.Accepted:
        return 'Принята';
      case PerformerBookingApplicationStatus.Declined:
        return 'Отклонена';
      case PerformerBookingApplicationStatus.Withdrawn:
        return 'Отозвана';
      default:
        return status ? String(status) : '—';
    }
  }

  private loadApplications(): void {
    const id = this.bookingCustomerId;
    if (!id) {
      return;
    }
    this.loading = true;
    this.loadError = null;
    this.cdr.markForCheck();
    this.bookingService
      .getVenueApplications(id, {
        page: this.page,
        size: this.pageSize,
        from: this.from || null,
        to: this.to || null,
        status: this.status || null
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
        untilDestroyed(this)
      )
      .subscribe(
        list => {
          this.items = list ?? [];
          this.hasMore = (list?.length ?? 0) >= this.pageSize;
        },
        err => {
          this.items = [];
          this.loadError = (err && (err.message || err.error?.message)) || 'Не удалось загрузить заявки.';
        }
      );
  }
}
